// Rédaction PII pré-egress, partagée par TOUTES les sources de diagnostic.
//
// Ce module existe pour une raison précise : `diagnostics-capture.ts` (qui
// patche console/fetch/XHR) et `diagnostics-observers.ts` (qui observe
// passivement les ressources) alimentent le MÊME blob. Tant que la rédaction
// vivait dans le seul module de capture, la source passive ne pouvait pas s'en
// servir sans créer un cycle d'import — et elle s'en passait, laissant fuiter
// les identifiants présents dans le chemin des URL d'images, scripts et polices.
// La garantie doit être unique, donc le code doit l'être aussi.
//
// rrweb masque le DOM (inputs starrés, zones bloquées) ; ces regex couvrent ce
// que le masquage DOM NE voit PAS : nos custom events (console, path d'URL) et
// tout texte qui échapperait aux sélecteurs. Filet de sécurité : la vraie valeur
// est remplacée DANS le navigateur, à l'écriture dans le ring — elle n'existe
// donc jamais en clair en mémoire au-delà de l'appel (PCI/HIPAA).

const EMAIL_RE = /[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}/g;
const SSN_RE = /\b\d{3}-\d{2}-\d{4}\b/g;
// Suite de 13–19 chiffres, séparateurs espace/tiret tolérés — validée par Luhn.
const CARD_RE = /\d(?:[ -]?\d){12,18}/g;

/** Validation Luhn d'une suite de chiffres (rejette les faux positifs). */
function luhnValid(digits: string): boolean {
  let sum = 0;
  let alt = false;
  for (let i = digits.length - 1; i >= 0; i--) {
    let n = digits.charCodeAt(i) - 48;
    if (n < 0 || n > 9) return false;
    if (alt) {
      n *= 2;
      if (n > 9) n -= 9;
    }
    sum += n;
    alt = !alt;
  }
  return sum % 10 === 0;
}

/** Remplace emails / SSN / cartes bancaires (Luhn) par des marqueurs. Idempotent
 *  et sûr sur chaîne vide. À appliquer à tout texte libre avant egress. */
export function redactPII(text: string): string {
  if (!text) return text;
  let out = text.replace(EMAIL_RE, "[email masqué]");
  out = out.replace(SSN_RE, "[id masqué]");
  out = out.replace(CARD_RE, (m) => {
    const digits = m.replace(/[ -]/g, "");
    return digits.length >= 13 && digits.length <= 19 && luhnValid(digits) ? "[carte masquée]" : m;
  });
  return out;
}

/** URL réduite à origin + chemin : on retire la query (peut porter des tokens). */
export function safeUrl(input: unknown): string {
  const raw =
    typeof input === "string"
      ? input
      : input instanceof URL
        ? input.href
        : input instanceof Request
          ? input.url
          : String(input);
  try {
    const u = new URL(raw, typeof location !== "undefined" ? location.href : undefined);
    // Query déjà retirée ; on rédige en plus les PII embarquées dans le path
    // (ex. /users/jane@corp.com/…).
    return u.origin + redactPII(u.pathname);
  } catch {
    return redactPII(raw.split("?")[0] ?? raw);
  }
}
